const OPENING = { brief: 0.45, held: 1.45, long: 2.8 };
const ENDING = { brief: 1.1, held: 2.4, long: 4.2 };

export const ROOM_SPACES = {
  close_left: { pan: -0.68, direct: 1, cutoff: 18000, reverb: 0.08, x: 24, y: 63 },
  left: { pan: -0.76, direct: 0.88, cutoff: 14500, reverb: 0.14, x: 18, y: 45 },
  distant_left: { pan: -0.58, direct: 0.58, cutoff: 6200, reverb: 0.32, x: 30, y: 27 },
  close_center: { pan: 0, direct: 1.02, cutoff: 18500, reverb: 0.07, x: 50, y: 69 },
  center: { pan: 0, direct: 0.86, cutoff: 14000, reverb: 0.15, x: 50, y: 47 },
  distant_center: { pan: 0, direct: 0.55, cutoff: 5800, reverb: 0.34, x: 50, y: 24 },
  close_right: { pan: 0.68, direct: 1, cutoff: 18000, reverb: 0.08, x: 76, y: 63 },
  right: { pan: 0.76, direct: 0.88, cutoff: 14500, reverb: 0.14, x: 82, y: 45 },
  distant_right: { pan: 0.58, direct: 0.58, cutoff: 6200, reverb: 0.32, x: 70, y: 27 },
};

function finiteDuration(value) {
  return Number.isFinite(value) ? Math.max(0.08, value) : 0.08;
}

function startForCue(cue, anchor, duration) {
  if (!anchor) return 0;
  const end = anchor.start + anchor.duration;
  switch (cue) {
    case 'immediate': return end + 0.08;
    case 'interrupt': return Math.max(anchor.start + 0.32, end - Math.min(0.9, anchor.duration * 0.27));
    case 'overlap_light': return Math.max(anchor.start + 0.24, end - Math.min(0.62, anchor.duration * 0.18));
    case 'together': return anchor.start + 0.06;
    case 'after_short_pause': return end + 1.05;
    case 'after_long_pause': return end + 2.65;
    case 'after_false_end': return end + 4.15;
    case 'after':
    default: return end + 0.42;
  }
}

export function resolveRoomTimeline(scene, clipsById) {
  const opening = OPENING[scene?.opening_silence] ?? OPENING.held;
  const resolved = [];
  const byId = new Map();
  for (const utterance of scene?.utterances || []) {
    const clip = clipsById instanceof Map ? clipsById.get(utterance.id) : clipsById?.[utterance.id];
    if (!clip) continue;
    const duration = finiteDuration(clip.duration);
    const anchor = byId.get(utterance.anchor_id) || resolved.at(-1);
    const relativeStart = utterance.timing === 'opening' ? 0 : startForCue(utterance.timing, anchor, duration);
    const item = { ...utterance, clip, duration, start: Math.max(0, relativeStart) };
    resolved.push(item);
    byId.set(item.id, item);
  }
  const shifted = resolved.map((item) => ({ ...item, start: item.start + opening }));
  for (const item of shifted) {
    const overlaps = shifted.filter((other) => (
      other.start < item.start + item.duration && item.start < other.start + other.duration
    )).length;
    item.mixGain = 1 / (1 + Math.max(0, overlaps - 1) * 0.18);
  }
  const spokenEnd = shifted.reduce((max, item) => Math.max(max, item.start + item.duration), opening);
  const duration = spokenEnd + (ENDING[scene?.ending_space] ?? ENDING.held);
  return { events: shifted, opening, spokenEnd, duration };
}

export function activeRoomSpeakers(timeline, seconds) {
  const active = new Set();
  for (const event of timeline?.events || []) {
    if (seconds >= event.start && seconds <= event.start + event.duration) active.add(event.speaker_id);
  }
  return active;
}
